// 261015 P4c-2c-2 (D9/D10/Q5/Q13) — phần THUẦN của "câu V.O. giới thiệu": trạng thái từng câu ở ② Tổng quan, nhãn + chỉ-đọc của beat INTRO ở ⑤.
// KHÔNG import antd (jest của CRA không nạp được antd v6). FE KHÔNG tự suy "câu dùng ở tập nào" — chỉ in lại `introLineUse` do BE tính (`introLineEligibility`).
import type { IntroLineUse, IntroReason, ScriptBeat } from '../../../types/adaptation';

/** 261015 P4c-2c-2 (Q5): sửa tay ⑤ trả 422 mã này (+ `violations`) khi beat V.O. giới thiệu bị đổi chữ/loại/người nói/chỗ đứng hoặc có beat INTRO mới. 0 ghi. */
export const MANUAL_INTRO_INVALID = 'MANUAL_INTRO_INVALID';

// ── ② danh sách câu: trạng thái từng câu ───────────────────────────────────────────────────────

export interface IntroLineStatusView {
  /** `ok` = dùng được (chữ nhạt); `warn` = không lên phim (thông báo nhẹ vàng, KHÔNG phải lỗi đỏ, không chặn chốt). */
  tone: 'ok' | 'warn';
  /** Một câu đủ ý, ví dụ "Dùng ở tập 3" / "Không dùng được: …". */
  headline: string;
  /** Chữ khi câu được dùng (đã thay tên) — CHỈ khi dùng được VÀ khác chữ gốc của câu (Q13). Không hứa "lên phim": câu là tuỳ chọn. */
  filmText: string | null;
}

const norm = (t: unknown): string => String(t ?? '').replace(/\s+/g, ' ').trim();

const chapterText = (ch: number | null | undefined): string => (typeof ch === 'number' && Number.isFinite(ch) ? `ch.${ch}` : 'một chương chưa rõ');

/** Lý do không dùng được → câu tiếng Việt kèm cách sửa. `BASIS_AFTER_EPISODE` đúng khuôn D9. */
export const introReasonText = (reason: IntroReason | string, basisChapter: number | null): string => {
  switch (reason) {
    case 'SUBJECT_NOT_ALLOWED':
      return 'Không dùng được: nhân vật / luật thế giới của câu không còn được giữ trong Tổng quan — sửa chủ thể của câu hoặc xoá câu';
    case 'NO_ANCHOR':
      return 'Không dùng được: chưa có chỗ trong truyện cho thấy nhân vật này xuất hiện lần đầu — thiếu mốc để đặt câu';
    case 'BASIS_NOT_ALLOWED':
      return 'Không dùng được: câu chưa có ý nền hợp lệ (thiếu ý nền, hoặc ý nền chưa có mốc chương) — sửa các ý nền của câu';
    case 'BASIS_AFTER_EPISODE':
      return `Không dùng được: ý nền lộ ở ${chapterText(basisChapter)}, sau tập nhân vật xuất hiện lần đầu — sửa câu dựa vào ý sớm hơn`;
    case 'NOT_IN_ANY_EPISODE':
      return `Không dùng được: chưa có tập nào đã chia chứa chỗ nhân vật / luật này xuất hiện (ý nền lộ ở ${chapterText(basisChapter)}) — chia thêm tập hoặc sửa câu`;
    default:
      return 'Không dùng được: chưa rõ lý do — mở lại Tổng quan để kiểm';
  }
};

/**
 * Trạng thái một câu. `use` vắng (BE cũ / chưa tính) ⇒ `null` (không in gì — không đoán). `originalText` = chữ lưu trong Tổng quan;
 * `use.text` = chữ SAU thay tên (chính chuỗi vào beat). Chỉ so khi khác thật (bỏ khác biệt khoảng trắng).
 */
export const introLineStatus = (use: IntroLineUse | null | undefined, originalText: string): IntroLineStatusView | null => {
  if (!use || typeof use !== 'object') return null;
  if (use.reason === 'OK') {
    const ep = typeof use.episode === 'number' ? use.episode : null;
    const film = norm(use.text);
    return {
      tone: 'ok',
      headline: ep !== null ? `Dùng ở tập ${ep}` : 'Dùng được (chưa rõ tập)',
      filmText: film && film !== norm(originalText) ? film : null,
    };
  }
  return { tone: 'warn', headline: introReasonText(use.reason, use.basisChapter ?? null), filmText: null };
};

// ── ⑤ beat INTRO ─────────────────────────────────────────────────────────────────────────────

/**
 * Beat V.O. giới thiệu ở FE = V.O. ∧ `introLineId` (Leader chốt: khoá chỉ đọc theo `introLineId`). KHÔNG đồng nghĩa BE `introBeat.ts:isIntroBeat` = V.O. ∧ `voiceoverPurpose` hợp lệ ∧ `introLineId`
 * — FE khoá RỘNG hơn (V.O. có id mà thiếu purpose cũng khoá); `prepareForSave` lột cờ khỏi beat không phải V.O. để hai bên không lệch ở thoại/hành động. Sửa tay: chỉ giữ hoặc bỏ cả beat.
 */
export const isIntroBeat = (b: Pick<ScriptBeat, 'kind' | 'introLineId'> | null | undefined): boolean =>
  !!b && b.kind === 'voiceover' && !!String(b.introLineId ?? '').trim();

/** Nhãn nhỏ ở trình đọc (D10) — có `voiceoverPurpose` hoặc `introLineId` trên beat V.O. Không có id ⇒ bỏ phần "câu đã duyệt". `null` = beat thường. */
export const introBeatLabel = (b: Pick<ScriptBeat, 'kind' | 'introLineId' | 'voiceoverPurpose'> | null | undefined): string | null => {
  if (!b || b.kind !== 'voiceover') return null;
  const id = String(b.introLineId ?? '').trim();
  if (!b.voiceoverPurpose && !id) return null;
  return id ? `V.O. giới thiệu · câu đã duyệt ${id}` : 'V.O. giới thiệu';
};

export const INTRO_READONLY_NOTE = 'Chữ lấy từ câu đã duyệt ở Tổng quan — không sửa ở đây; muốn đổi thì sửa câu ở Tổng quan, hoặc bỏ câu này khỏi tập.';
export const INTRO_REMOVE_LABEL = 'Bỏ câu này';

// ── 422 của sửa tay ───────────────────────────────────────────────────────────────────────────

/** Câu hiện ở panel lỗi của trình sửa tay từ body 422: ưu tiên `violations[].message`; `MANUAL_INTRO_INVALID` mà thiếu `violations` ⇒ câu chung. Không phải 422 ⇒ `null`. */
export const manualRejectionLines = (status: number | undefined, data: unknown): string[] | null => {
  if (status !== 422) return null;
  const d = (data ?? {}) as { code?: string; message?: string; violations?: Array<{ message?: string }> };
  const lines = (Array.isArray(d.violations) ? d.violations : []).map((v) => norm(v?.message)).filter(Boolean);
  if (lines.length) return lines;
  if (d.code === MANUAL_INTRO_INVALID) return [norm(d.message) || 'Câu V.O. giới thiệu bị đổi hoặc đặt sai chỗ — chỉ được giữ nguyên hoặc bỏ cả câu.'];
  return null;
};
